import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LogOut,
  Plane,
  Moon,
  Sun,
  LayoutDashboard,
  Users,
  Repeat,
  Settings,
  Coins,
  ScrollText,
  Plane as PlaneIcon,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useTheme } from "@/lib/theme-context";
import { Button } from "@/components/ui/button";
import AdminProfileDialog from "@/components/AdminProfileDialog";
import { useBranding, logoSrc } from "@/lib/branding";

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="theme-toggle"
      aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      className="h-9 w-9 rounded-full grid place-items-center transition-colors"
      style={{
        background: "var(--surface-2)",
        border: "1px solid var(--border)",
        color: "var(--text)",
      }}
    >
      {theme === "dark" ? (
        <Sun className="h-4 w-4" strokeWidth={2} />
      ) : (
        <Moon className="h-4 w-4" strokeWidth={2} />
      )}
    </button>
  );
}

function tabsForRole(role) {
  if (role === "customer") {
    return [
      { to: "/customer", label: "Rewards", icon: LayoutDashboard, testid: "tab-customer-home" },
      { to: "/customer/book", label: "Book a Trip", icon: PlaneIcon, testid: "tab-customer-book" },
    ];
  }
  if (role === "staff") {
    return [
      { to: "/staff", label: "Front Desk", icon: Users, testid: "tab-staff-home" },
    ];
  }
  return [
    { to: "/admin?view=stats", label: "Overview", icon: LayoutDashboard, testid: "tab-admin-stats" },
    { to: "/admin?view=customers", label: "Members", icon: Users, testid: "tab-admin-customers" },
    { to: "/admin?view=transactions", label: "Ledger", icon: ScrollText, testid: "tab-admin-ledger" },
    { to: "/admin?view=config", label: "Config", icon: Settings, testid: "tab-admin-config" },
  ];
}

function BottomNav({ role }) {
  const tabs = tabsForRole(role);
  const { search, pathname } = useLocation();
  if (tabs.length <= 1) return null;
  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t"
      style={{
        background: "var(--surface)",
        borderColor: "var(--border)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
      data-testid="bottom-nav"
    >
      <ul className="grid" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
        {tabs.map((t) => {
          const Icon = t.icon;
          const [tp, tq] = t.to.split("?");
          const active = pathname === tp && (search.includes(tq || "") || !tq);
          return (
            <li key={t.to}>
              <NavLink
                to={t.to}
                data-testid={t.testid}
                className="flex flex-col items-center justify-center gap-1 py-3 text-[10px]"
                style={{
                  color: active ? "var(--gold)" : "var(--text-muted)",
                }}
              >
                <Icon className="h-5 w-5" strokeWidth={active ? 2.4 : 1.8} />
                <span
                  className="tracking-wider"
                  style={{ letterSpacing: "0.06em" }}
                >
                  {t.label}
                </span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function CustomerTopNav() {
  const { pathname } = useLocation();
  return (
    <nav className="hidden md:flex items-center gap-1 mr-2" data-testid="customer-top-nav">
      {tabsForRole("customer").map((t) => (
        <NavLink key={t.to} to={t.to} data-testid={`${t.testid}-desktop`}
                 className="px-3 h-9 inline-flex items-center gap-1.5 rounded-full text-sm font-medium transition-colors"
                 style={pathname === t.to ? { background: "var(--navy)", color: "#fff" } : { color: "var(--text-muted)" }}>
          <t.icon className="h-4 w-4" /> {t.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AppShell({ title, subtitle, children, actions }) {
  const { user, logout } = useAuth();
  const branding = useBranding();
  const nav = useNavigate();

  const handleLogout = () => {
    logout();
    nav("/");
  };

  const roleLabel =
    user?.role === "admin"
      ? "Admin"
      : user?.role === "staff"
        ? "Front Desk"
        : "Members";

  return (
    <div
      className="min-h-screen"
      style={{ background: "var(--bg)", color: "var(--text)" }}
    >
      <header
        className="sticky top-0 z-30"
        style={{
          background: "var(--bg)",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={logoSrc(branding.logo_light)}
              alt="Zola Safari Tours"
              className="brand-logo h-11 w-auto"
              data-testid="appshell-logo"
            />
            <div>
              <div className="text-base font-semibold" style={{ letterSpacing: "-0.01em" }}>
                Zola <span className="gold">Rewards</span>
              </div>
              <div className="label-caps mt-0.5">{roleLabel} · India &amp; UAE</div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {user?.role === "customer" && <CustomerTopNav />}
            <div className="hidden sm:block text-right mr-1">
              <div className="text-sm font-medium">{user?.name}</div>
              <div className="label-caps mt-0.5">{user?.role}</div>
            </div>
            <ThemeToggle />
            {(user?.role === "staff" || user?.role === "admin") && <AdminProfileDialog />}
            <Button
              size="sm"
              onClick={handleLogout}
              data-testid="logout-button"
              className="rounded-full h-9 px-4"
              style={{
                background: "transparent",
                border: "1px solid var(--border-strong)",
                color: "var(--text)",
              }}
            >
              <LogOut className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-10 pt-8 pb-tab">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <h1
              className="text-2xl sm:text-3xl font-bold"
              style={{ letterSpacing: "-0.02em" }}
            >
              {title}
            </h1>
            {subtitle && (
              <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>
                {subtitle}
              </p>
            )}
          </div>
          <div>{actions}</div>
        </div>
        {children}
      </main>

      <BottomNav role={user?.role} />
    </div>
  );
}
